import React, { useEffect, useContext, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from '@/helpers/Link';
import { modalContext } from '@/context/modal.context';
import t from '@/utils/Language/UI-translations';
import { LANGUAGES } from '@/utils/Language';
import { languageContext } from '@/context/language.context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropdownSubmenu } from '@/components/DropdownSubmenu/DropdownSubmenu';
import TagIcons from '@/utils/tags_icons.json';
import useLocation from '@/hooks/useLocation';
import { viewportContext } from '@/context/viewport.context';
import useAccount from '@/hooks/useAccount';
import GlobalBanner from '@/components/GlobalBanner/GlobalBanner';
import useHoneybadger from '@/hooks/useHoneybadger';
import useMenuOutsideClickToClose from '@/hooks/useMenuOutsideClickToClose';
import { useRouter } from 'next/router';
import HeaderLogo from './HeaderLogo/HeaderLogo';
import HeaderMenu from './HeaderMenu/HeaderMenu';
import LoginButtons from './LoginButtons/LoginButtons';
import LoggedInMenuList from './LoggedInMenuList/LoggedInMenuList';
import BrowseResourcesMenu from './BrowseResourcesMenu/BrowseResourcesMenu';
import { SolutionContext } from '@/context/solution.context';
import 'react-bootstrap-submenu/dist/index.css';
import Button from '@/components/Button/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Row from 'react-bootstrap/Row';
import { searchHelpers } from '@/utils/helpers';
import classes from './Header.module.scss';
const AutoComplete = dynamic(() => import('@/features/googleMap/components/GoogleLocation/AutoComplete'), {
  ssr: false,
  loading: () => (
    <input
      className={`${classes.autocompleteStyles} ${classes.fixedHeight}`}
      placeholder={t('Address, city, or zip')}
      disabled
      readOnly
      aria-hidden="true"
    />
  )
});

const Header = () => {
  const { currentLanguage, setLanguage } = useContext(languageContext);
  const autoCompleteElementId = 'header-search-autocomplete';
  const { solution } = useContext(SolutionContext);
  const { locationContext } = useLocation();
  const { locationHelpers } = locationContext;
  const { enqueueModal } = useContext(modalContext);
  const { isXl, isMobileOrTablet, isDesktop } = useContext(viewportContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasOpenedMenuOnce, setHasOpenedMenuOnce] = useState(false);
  const { account, logout } = useAccount();
  const { isLoggedIn } = account;
  const [searchTerm, setSearchTerm] = useState('');
  const { notifyHoneybadger } = useHoneybadger();
  const router = useRouter();
  const regionCheckMounted = useRef(false);

  const showSearch = solution.includes?.nav_search_bar;
  const showBrowseResourcesMenu = solution.includes?.browse_resources_menu;
  const totalBtns = showBrowseResourcesMenu ? 2 : 1; // Menu and possibly Browse Resources
  const openLocationModal = () => {
    setIsMenuOpen(false);
    enqueueModal({
      name: 'UpdateLocationModal',
      account: account,
      locationContext: locationContext
    });
  };

  useEffect(() => {
    // Skip the region check on initial mount. The Header remounts between page
    // navigations when the pages use different layout trees, causing this effect to
    // fire with an already-mismatched region and incorrectly showing the UpdateRegionModal.
    // We only want the modal when the region genuinely changes after the component is mounted.
    if (!regionCheckMounted.current) {
      regionCheckMounted.current = true;
      return;
    }
    if (account.data?.region && locationContext.region != account.data?.region) {
      enqueueModal({
        name: 'UpdateRegionModal',
        account: account,
        currentRegion: locationHelpers.getRegionObjectFromId(account.data.region),
        newRegion: locationHelpers.getRegionObjectFromId(locationContext.region)
      });
    }
  }, [locationContext.region]);

  useEffect(() => {
    const element = document.getElementById('locationSearch');
    if (element) {
      element.autocomplete = 'off';
    }
  }, [isLoggedIn]);

  useMenuOutsideClickToClose(isMenuOpen, setIsMenuOpen);

  const doSearch = (e) => {
    e.preventDefault();

    const lat = locationContext.location.lat;
    const lng = locationContext.location.lng;

    if (typeof lat == 'undefined' || typeof lng == 'undefined') {
      notifyHoneybadger('React location lat lng is undefined', {
        message: 'React location lat lng is undefined',
        component: 'Header',
        context: {
          location: locationContext.location,
          currentPage: router.pathname
        }
      });
    }

    window.location.href = searchHelpers.searchTagLink(searchTerm, { lat, lng });
  };

  const onLogout = () => {
    setIsMenuOpen(false);
    logout();
  };

  const renderSearch = () => {
    if (!showSearch) return null;
    return (
      <>
        <form method="GET" onSubmit={doSearch} className={classes.searchForm}>
          <div className={`${classes.inputWrapper} ${classes.fixedHeight}`}>
            <Button
              type="secondary"
              shape="pill"
              icon={{ style: 'fas', font: 'search' }}
              onButtonClick={doSearch}
              additionalClasses={`${classes.btnSearchMobile} ${classes.headerBtn} headerBtn`}
              text={''}
              ariaLabel={t('Search')}
            />

            <input
              className={`${classes.searchInputStyles} ${classes.fixedHeight}`}
              name="tags"
              placeholder={t('Food pantries, housing search')}
              onChange={(e) => setSearchTerm(e.target.value)}
              type="search"
              aria-label={t('Search resources')}
            ></input>
          </div>
        </form>
        <div className={`d-none d-md-inline-block ${classes.inputWrapper} ${classes.autoCompleteWrapper}`}>
          <label htmlFor={autoCompleteElementId} className={classes.locationIcon}>
            <FontAwesomeIcon icon={['fas', 'location-dot']} />
          </label>
          <AutoComplete
            elementId={autoCompleteElementId}
            onChange={updateLocationContext}
            placeholder={t('Address, city, or zip')}
            additionalClasses={`${classes.autocompleteStyles} ${classes.fixedHeight}`}
          />
        </div>
        <div className={'d-none d-md-inline-block'}>
          <Button
            type="default"
            shape="pill"
            onButtonClick={doSearch}
            groupPosition="right"
            additionalClasses={`${classes.btnSearch} ${classes.headerBtn} ${classes.fixedHeight} headerBtn`}
            text={t('Find')}
          />
        </div>
      </>
    );
  };

  const updateLocationContext = (place) => {
    if (place?.address_components === undefined) {
      return false;
    }
    locationContext.updateLocationByPlace(place);
  };

  const nestedDropDownItem = (tags) => {
    let items = [];
    let subItems = [];
    if (Object.prototype.toString.call(tags) == '[object Object]' && Object.keys(tags).length <= 5) {
      Object.keys(tags).forEach((tagKey, index) => {
        subItems.push(
          <NavDropdown.Item href={searchHelpers.searchTagLink(tagKey)} key={tagKey + index}>
            <span className="parent-item">{t(tagKey)}</span>
          </NavDropdown.Item>
        );
        tags[tagKey].forEach((subTagKey, index) => {
          subItems.push(
            <NavDropdown.Item href={searchHelpers.searchTagLink(subTagKey)} key={subTagKey + index}>
              {t(subTagKey)}
            </NavDropdown.Item>
          );
        });
      });
    } else {
      Object.keys(tags).forEach((tagKey, index) => {
        if (typeof tags[tagKey] == 'object') {
          let displayTitle = TagIcons[tagKey] ? (
            <>
              <FontAwesomeIcon icon={['fas', TagIcons[tagKey]]} aria-hidden="true" />
              &nbsp;&nbsp;{t(tagKey)}
              <span className="right">
                <FontAwesomeIcon icon={['fa', 'angle-right']} aria-hidden="true" />
              </span>
            </>
          ) : (
            <>
              {t(tagKey)}
              <span className="right">
                <FontAwesomeIcon icon={['fa', 'angle-right']} aria-hidden="true" />
              </span>
            </>
          );
          items.push(
            <DropdownSubmenu show={false} key={tagKey + index} title={displayTitle}>
              {nestedDropDownItem(tags[tagKey])}
            </DropdownSubmenu>
          );
        } else {
          items.push(
            <NavDropdown.Item key={tagKey + index} href={searchHelpers.searchTagLink(tags[tagKey])}>
              {t(tags[tagKey])}
            </NavDropdown.Item>
          );
        }
      });
    }

    if (subItems.length > 6) {
      let breakPoint = Math.ceil(subItems.length / 2);
      items.push(
        <Row key={'subItems'} className={classes.browseSubMenu}>
          <Col xs="6" className="text-wrap">
            {subItems.slice(0, breakPoint)}
          </Col>
          <Col xs="6" className="text-wrap">
            {subItems.slice(breakPoint, subItems.length)}
          </Col>
        </Row>
      );
    } else {
      items.push(subItems);
    }

    return items;
  };

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <div className={classes.left} ref={ref}>
      <Button
        type="default"
        shape="pill"
        onButtonClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
        additionalClasses={`${classes.btnMenu} ${classes.headerBtn} ${classes.fixedHeight} headerBtn`}
        groupPosition={totalBtns === 1 ? '' : 'left'}
        icon={{ font: 'caret-down', position: 'right' }}
        text={t('Menu')}
      >
        {children}
      </Button>
    </div>
  ));

  return (
    <>
      <header className={`${classes.header} header`}>
        <Container fluid className={classes.headerContainer}>
          <HeaderLogo t={t} />
          <div className={`${classes.left} d-none d-xl-flex`}>
            {isLoggedIn && (
              <div>
                <Dropdown>
                  <Dropdown.Toggle as={CustomToggle} />
                  <Dropdown.Menu className={`dropdown-menu`} popperConfig={{ strategy: 'fixed' }}>
                    <LoggedInMenuList t={t} classes={classes} mobile={false} onCloseMenu={() => setIsMenuOpen(false)} />
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
            <BrowseResourcesMenu t={t} classes={classes} nestedDropDownItem={nestedDropDownItem} />
          </div>
          <div className={classes.menuSearch}>{renderSearch()}</div>
          <div className={classes.menuRight}>
            {isLoggedIn && (
              <Dropdown className="d-none d-xl-inline-block">
                <Dropdown.Toggle as="div" className={classes.languageDropdown}>
                  <FontAwesomeIcon className={classes.accountIcon} icon={['fa', 'globe']} />
                  <Button
                    type={'link'}
                    icon={{ style: 'fa', font: 'caret-down', position: 'right' }}
                    additionalClasses={classes.languageBtn}
                    text={LANGUAGES.find((l) => l.value === currentLanguage)?.label || currentLanguage}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                  {LANGUAGES.map((language) => (
                    <Dropdown.Item key={language.value} onClick={() => setLanguage(language.value)}>
                      {t(language.value)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
            {!isLoggedIn && (
              <Dropdown className="d-none d-xl-inline-block">
                <Dropdown.Toggle as="div" className={classes.languageDropdown}>
                  <FontAwesomeIcon className={classes.accountIcon} icon={['fa', 'globe']} />
                  <Button
                    type={'link'}
                    icon={{ style: 'fa', font: 'caret-down', position: 'right' }}
                    additionalClasses={classes.languageBtn}
                    text={LANGUAGES.find((l) => l.value === currentLanguage)?.label || currentLanguage}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                  {LANGUAGES.map((language) => (
                    <Dropdown.Item key={language.value} onClick={() => setLanguage(language.value)}>
                      {t(language.value)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
            <HeaderMenu t={t} classes={classes} onLogout={onLogout} />
            <span data-cy="header-menu-mobile" className={`d-xl-none`}>
              <Button
                type="link"
                icon={{ style: 'fa', font: 'bars' }}
                onButtonClick={() => {
                  setIsMenuOpen(true);
                  setHasOpenedMenuOnce(true);
                }}
                additionalClasses={`${classes.mobileMenuBtn} mobileMenuBtn`}
                ariaLabel={t('Open menu')}
                ariaExpanded={isMenuOpen}
                ariaControls="mobileMenuDrawer"
              />
            </span>
            <LoginButtons t={t} classes={classes} mobile={false} onCloseMenu={() => setIsMenuOpen(false)} />
          </div>
        </Container>
        {(isMobileOrTablet || (isDesktop && !isXl)) && (
          <div
            data-cy="mobile-drawer"
            id="mobileMenuDrawer"
            role="navigation"
            aria-label={t('Mobile menu')}
            aria-hidden={!isMenuOpen}
            style={{ right: isMenuOpen ? '0' : '-1800px' }}
            className={classes.mobileMenu}
          >
            <div className={classes.mobileMenuLogo}>
              <Row>
                <Col xs="2">
                  <Button
                    type="link"
                    icon={{ style: 'fa', font: 'chevron-left' }}
                    onButtonClick={() => setIsMenuOpen(false)}
                    ariaLabel={t('Close menu')}
                    additionalClasses={classes.mobileMenuClose}
                  />
                </Col>
                <Col xs="10">
                  <HeaderLogo t={t} version="mobile/tablet" />
                </Col>
              </Row>
            </div>
            <div className={classes.mobileMenuList}>
              <ul className={classes.mobileMenuItems}>
                <li className={classes.mobileMenuItem}>
                  <a
                    className={classes.mobileMenuLink}
                    onClick={openLocationModal}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openLocationModal()}
                  >
                    <FontAwesomeIcon icon={['fa', 'location-arrow']} aria-hidden="true" /> &nbsp;
                    {locationContext.location?.locationName}
                  </a>
                </li>
                {showBrowseResourcesMenu && (
                  <li className={classes.mobileMenuItem}>
                    <Link className={classes.mobileMenuLink} href="/browse">
                      <FontAwesomeIcon icon={['fa', 'th']} aria-hidden="true" /> &nbsp;
                      {t('Browse All Categories')}
                    </Link>
                  </li>
                )}
                {account?.data?.is_professional && account?.data?.affiliation && (
                  <li className={classes.mobileMenuItem}>
                    <a className={classes.mobileMenuLink} href={'/org/' + account.data.affiliation.fetchable_id}>
                      <FontAwesomeIcon icon={['fa', 'sitemap']} aria-hidden="true" /> &nbsp;
                      {account.data.affiliation.organization_name}
                    </a>
                  </li>
                )}
              </ul>
              {hasOpenedMenuOnce && isLoggedIn && (
                <>
                  <hr />
                  <LoggedInMenuList t={t} classes={classes} mobile={true} onCloseMenu={() => setIsMenuOpen(false)} />
                </>
              )}
              <hr />
              <ul className={classes.mobileMenuItems}>
                <Dropdown as="li" className={classes.mobileMenuItem}>
                  <Dropdown.Toggle as="div" className={`${classes.languageDropdown} ${classes.langDropdownMobile}`}>
                    <FontAwesomeIcon className={classes.accountIcon} icon={['fa', 'globe']} />
                    <Button
                      type={'link'}
                      additionalClasses={classes.langSelector}
                      text={LANGUAGES.find((l) => l.value === currentLanguage)?.label || currentLanguage}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={classes.langMenu} popperConfig={{ strategy: 'fixed' }} renderOnMount>
                    {LANGUAGES.map((language) => (
                      <Dropdown.Item key={language.value} className={classes.langMenuItem} onClick={() => setLanguage(language.value)}>
                        {t(language.value)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {isLoggedIn && (
                  <>
                    <li className={classes.mobileMenuItem}>
                      <Link className={classes.mobileMenuLink} href="/settings">
                        <FontAwesomeIcon icon={['fa', 'cog']} aria-hidden="true" /> &nbsp;
                        {t('Settings')}
                      </Link>
                    </li>
                    <li className={classes.mobileMenuItem}>
                      <Button
                        type="link"
                        icon={{ style: 'fa', font: 'sign-out-alt' }}
                        onButtonClick={onLogout}
                        text={t('Log out')}
                        additionalClasses={classes.mobileMenuActionBtn}
                      />
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        )}
      </header>
      <GlobalBanner />
    </>
  );
};

export default Header;
